import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  Car, Search, Filter, ArrowUpDown, ArrowUp, ArrowDown, 
  Eye, AlertTriangle, Building2, User, Calendar, RefreshCw 
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VehiclesManagement = ({ getAuthHeaders }) => {
  const [vehicles, setVehicles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [makeFilter, setMakeFilter] = useState('all');
  const [modelFilter, setModelFilter] = useState('all');
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  
  // Sorting state
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [refreshingVehicle, setRefreshingVehicle] = useState(null);
  
  // Vehicle edit dialog state
  const [isVehicleDialogOpen, setIsVehicleDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [vehicleFormData, setVehicleFormData] = useState({});
  const [contacts, setContacts] = useState([]);
  const [isDvlaLookupLoading, setIsDvlaLookupLoading] = useState(false);

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API}/vehicles/all`, getAuthHeaders());
      setVehicles(response.data);
    } catch (error) {
      console.error('Failed to fetch vehicles:', error);
      toast.error('Failed to load vehicles');
    } finally {
      setIsLoading(false);
    }
  };

  // Sorting functions
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4" />;
    return sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />;
  };

  const getVehicleTypeIcon = (vehicleType) => {
    switch (vehicleType) {
      case 'van':
      case 'truck':
        return '🚐';
      case 'motorcycle':
        return '🏍️';
      case 'motorhome':
        return '🚌';
      case 'suv':
        return '🚙';
      case 'convertible':
        return '🏎️';
      default:
        return '🚗';
    }
  };

  const getMotStatus = (vehicle) => {
    if (!vehicle.mot_expiry_date) return { status: 'unknown', label: 'No MOT date', color: 'gray' };
    
    const today = new Date();
    const motExpiry = new Date(vehicle.mot_expiry_date);
    const daysUntilExpiry = Math.ceil((motExpiry - today) / (1000 * 60 * 60 * 24));
    
    // Format date as DD/MM/YYYY
    const formatDate = (dateString) => {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };
    
    if (daysUntilExpiry < 0) {
      return { status: 'overdue', label: `Expired ${Math.abs(daysUntilExpiry)} days ago`, color: 'red' };
    } else if (daysUntilExpiry <= 30) {
      return { status: 'due_soon', label: `Expires in ${daysUntilExpiry} days`, color: 'orange' };
    } else {
      return { status: 'ok', label: `Expires ${formatDate(vehicle.mot_expiry_date)}`, color: 'green' };
    }
  };

  // Refresh MOT status from DVLA API
  const handleRefreshMot = async (vehicle) => {
    setRefreshingVehicle(vehicle.id);
    try {
      // Call DVLA API
      const dvlaResponse = await axios.post(
        `${API}/vehicles/dvla-lookup`,
        { registration_number: vehicle.registration_number },
        getAuthHeaders()
      );

      if (dvlaResponse.data.success && dvlaResponse.data.data) {
        const dvlaData = dvlaResponse.data.data;
        
        // Update vehicle with new MOT data
        const updatePayload = {
          mot_status: dvlaData.motStatus || vehicle.mot_status,
          mot_expiry_date: dvlaData.motExpiryDate && !dvlaData.motExpiryDate.includes('<1 YEAR FROM NOW>') 
            ? dvlaData.motExpiryDate 
            : vehicle.mot_expiry_date,
          tax_status: dvlaData.taxStatus || vehicle.tax_status,
        };

        await axios.put(
          `${API}/vehicles/${vehicle.id}`,
          { ...vehicle, ...updatePayload },
          getAuthHeaders()
        );

        toast.success('✅ MOT status refreshed from DVLA');
        fetchVehicles(); // Reload the list
      } else {
        toast.error(dvlaResponse.data.user_message || 'Failed to refresh MOT status');
      }
    } catch (error) {
      console.error('Error refreshing MOT:', error);
      toast.error('Failed to refresh MOT status from DVLA');
    } finally {
      setRefreshingVehicle(null);
    }
  };

  const openVehicleDialog = async (vehicle) => {
    setEditingVehicle(vehicle);
    
    // Fetch contacts for this customer
    try {
      const response = await axios.get(`${API}/contacts?customer_id=${vehicle.customer_id}`, getAuthHeaders());
      setContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    }
    
    setVehicleFormData({
      contact_id: vehicle.contact_id || '__no_contact__',
      registration_number: vehicle.registration_number || '',
      make: vehicle.make || '',
      model: vehicle.model || '',
      year: vehicle.year?.toString() || '',
      color: vehicle.color || '',
      vehicle_type: vehicle.vehicle_type || '',
      body_type: vehicle.body_type || '__not_specified__',
      fuel_type: vehicle.fuel_type || '__not_specified__',
      transmission: vehicle.transmission || '__not_specified__',
      engine_size: vehicle.engine_size || '',
      mileage: vehicle.mileage?.toString() || '',
      vin_number: vehicle.vin_number || '',
      insurance_company: vehicle.insurance_company || '',
      insurance_renewal_date: vehicle.insurance_renewal_date || '',
      mot_expiry_date: vehicle.mot_expiry_date || '',
      mot_status: vehicle.mot_status || '',
      tax_status: vehicle.tax_status || '',
      co2_emissions: vehicle.co2_emissions?.toString() || '',
      special_requirements: vehicle.special_requirements || '',
      notes: vehicle.notes || ''
    });
    
    setIsVehicleDialogOpen(true);
  };

  const handleVehicleSubmit = async (e) => {
    e.preventDefault();
    
    if (!vehicleFormData.registration_number?.trim()) {
      toast.error('Registration number is required');
      return;
    }

    try {
      const submitData = { ...vehicleFormData };
      submitData.customer_id = editingVehicle.customer_id;
      submitData.company_id = editingVehicle.company_id;
      
      // Handle special dropdown values
      if (submitData.contact_id === '__no_contact__') {
        submitData.contact_id = null;
      }
      if (submitData.body_type === '__not_specified__') {
        submitData.body_type = null;
      }
      if (submitData.fuel_type === '__not_specified__') {
        submitData.fuel_type = null;
      }
      if (submitData.transmission === '__not_specified__') {
        submitData.transmission = null;
      }
      
      // Convert empty strings to null for optional fields
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '') {
          submitData[key] = null;
        }
      });

      console.log('Submitting vehicle update:', submitData);

      await axios.put(`${API}/vehicles/${editingVehicle.id}`, submitData, getAuthHeaders());
      
      toast.success('Vehicle updated successfully');
      setIsVehicleDialogOpen(false);
      fetchVehicles();
    } catch (error) {
      console.error('Failed to update vehicle:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Failed to update vehicle';
      toast.error(errorMessage);
    }
  };

  const handleDvlaLookup = async () => {
    const regNumber = vehicleFormData.registration_number?.trim();
    
    if (!regNumber) {
      toast.error('Please enter a registration number first');
      return;
    }

    setIsDvlaLookupLoading(true);
    
    try {
      const response = await axios.post(`${API}/vehicles/dvla-lookup`, 
        { registration_number: regNumber }, 
        getAuthHeaders()
      );
      
      if (response.data.success) {
        const dvlaData = response.data.data;
        
        setVehicleFormData({
          ...vehicleFormData,
          make: dvlaData.make || vehicleFormData.make,
          model: dvlaData.model || vehicleFormData.model,
          color: dvlaData.colour || vehicleFormData.color,
          year: dvlaData.yearOfManufacture?.toString() || vehicleFormData.year,
          fuel_type: dvlaData.fuelType || vehicleFormData.fuel_type,
          mot_expiry_date: dvlaData.motExpiryDate || vehicleFormData.mot_expiry_date,
          mot_status: dvlaData.motStatus || vehicleFormData.mot_status,
          tax_status: dvlaData.taxStatus || vehicleFormData.tax_status,
          co2_emissions: dvlaData.co2Emissions?.toString() || vehicleFormData.co2_emissions,
        });
        
        toast.success('✅ Vehicle details found and populated!', { duration: 3000 });
      } else {
        toast.error(response.data.message || 'Vehicle not found');
      }
    } catch (error) {
      console.error('DVLA lookup error:', error);
      toast.error(error.response?.data?.message || 'Failed to lookup vehicle');
    } finally {
      setIsDvlaLookupLoading(false);
    }
  };

  // Helper to get field styling based on whether it's filled
  const getFieldClassName = (value, baseClass = '') => {
    const hasValue = value && value !== '__no_contact__' && value !== '__not_specified__';
    return `${baseClass} ${hasValue ? 'bg-green-50 border-green-300' : ''}`;
  };

  // Filter and sort vehicles
  const filteredAndSortedVehicles = useMemo(() => {
    let filtered = vehicles;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(vehicle =>
        vehicle.registration_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vehicle.make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vehicle.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vehicle.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply filters
    if (makeFilter && makeFilter !== 'all') {
      filtered = filtered.filter(vehicle => 
        vehicle.make?.toLowerCase().includes(makeFilter.toLowerCase())
      );
    }
    
    if (modelFilter && modelFilter !== 'all') {
      filtered = filtered.filter(vehicle => 
        vehicle.model?.toLowerCase().includes(modelFilter.toLowerCase())
      );
    }
    
    if (vehicleTypeFilter && vehicleTypeFilter !== 'all') {
      filtered = filtered.filter(vehicle => vehicle.vehicle_type === vehicleTypeFilter);
    }
    
    if (yearFilter && yearFilter !== 'all') {
      filtered = filtered.filter(vehicle => vehicle.year?.toString() === yearFilter);
    }

    // Apply sorting
    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;
        
        switch (sortField) {
          case 'registration':
            aValue = a.registration_number || '';
            bValue = b.registration_number || '';
            break;
          case 'make_model':
            aValue = `${a.make || ''} ${a.model || ''}`;
            bValue = `${b.make || ''} ${b.model || ''}`;
            break;
          case 'customer':
            aValue = a.customer_name || '';
            bValue = b.customer_name || '';
            break;
          case 'year':
            aValue = a.year || 0;
            bValue = b.year || 0;
            break;
          case 'service_due':
          case 'mot_expiry':
            aValue = a.mot_expiry_date ? new Date(a.mot_expiry_date).getTime() : 0;
            bValue = b.mot_expiry_date ? new Date(b.mot_expiry_date).getTime() : 0;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [vehicles, searchTerm, makeFilter, modelFilter, vehicleTypeFilter, yearFilter, sortField, sortDirection]);

  // Get unique values for filters
  const uniqueMakes = [...new Set(vehicles.map(v => v.make).filter(Boolean))].sort();
  const uniqueModels = [...new Set(vehicles.map(v => v.model).filter(Boolean))].sort();
  const uniqueVehicleTypes = [...new Set(vehicles.map(v => v.vehicle_type).filter(Boolean))].sort();
  const uniqueYears = [...new Set(vehicles.map(v => v.year).filter(Boolean))].sort((a, b) => b - a);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold" style={{ fontFamily: 'Inter, sans-serif' }}>Vehicles Database</h2>
          <p className="text-gray-500">All vehicles across your customers for insights and marketing</p>
        </div>
      </div>

      {/* Vehicle Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">{vehicles.length}</div>
          <div className="text-sm text-gray-600">Total Vehicles</div>
        </Card>
        <Card className="p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{uniqueMakes.length}</div>
          <div className="text-sm text-gray-600">Different Makes</div>
        </Card>
        <Card className="p-4 text-center">
          <div className="text-2xl font-bold text-orange-600">
            {vehicles.filter(v => {
              const status = getMotStatus(v);
              return status.status === 'due_soon' || status.status === 'overdue';
            }).length}
          </div>
          <div className="text-sm text-gray-600">MOT Due Soon</div>
        </Card>
        <Card className="p-4 text-center">
          <div className="text-2xl font-bold text-purple-600">
            {vehicles.filter(v => v.year >= new Date().getFullYear() - 3).length}
          </div>
          <div className="text-sm text-gray-600">Recent Models</div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold">Filters</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search vehicles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div>
              <Select value={makeFilter} onValueChange={setMakeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Makes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Makes</SelectItem>
                  {uniqueMakes.map(make => (
                    <SelectItem key={make} value={make}>{make}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Select value={modelFilter} onValueChange={setModelFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Models" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Models</SelectItem>
                  {uniqueModels.map(model => (
                    <SelectItem key={model} value={model}>{model}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Select value={vehicleTypeFilter} onValueChange={setVehicleTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {uniqueVehicleTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Select value={yearFilter} onValueChange={setYearFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Years" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  {uniqueYears.map(year => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Filter Results Summary */}
          {(searchTerm || (makeFilter && makeFilter !== 'all') || (modelFilter && modelFilter !== 'all') || (vehicleTypeFilter && vehicleTypeFilter !== 'all') || (yearFilter && yearFilter !== 'all')) && (
            <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
              Showing {filteredAndSortedVehicles.length} of {vehicles.length} vehicles
              {searchTerm && ` matching "${searchTerm}"`}
              {makeFilter && makeFilter !== 'all' && ` from ${makeFilter}`}
              {modelFilter && modelFilter !== 'all' && ` model ${modelFilter}`}
            </div>
          )}
        </div>
      </Card>

      {/* Vehicles Table */}
      {isLoading ? (
        <div className="text-center py-12">Loading...</div>
      ) : vehicles.length === 0 ? (
        <Card className="p-12 text-center">
          <Car className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No vehicles found in your database yet</p>
          <p className="text-xs text-gray-400 mt-2">
            Add vehicles to customer records to see them here
          </p>
        </Card>
      ) : filteredAndSortedVehicles.length === 0 ? (
        <Card className="p-12 text-center">
          <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No vehicles found matching the current filters.</p>
          <Button 
            variant="outline" 
            onClick={() => {
              setSearchTerm('');
              setMakeFilter('all');
              setModelFilter('all');
              setVehicleTypeFilter('all');
              setYearFilter('all');
            }} 
            className="mt-4"
          >
            Clear Filters
          </Button>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('registration')}
                    className="h-auto p-0 font-semibold hover:bg-transparent"
                  >
                    Registration {getSortIcon('registration')}
                  </Button>
                </TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('make_model')}
                    className="h-auto p-0 font-semibold hover:bg-transparent"
                  >
                    Make & Model {getSortIcon('make_model')}
                  </Button>
                </TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('year')}
                    className="h-auto p-0 font-semibold hover:bg-transparent"
                  >
                    Year {getSortIcon('year')}
                  </Button>
                </TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('customer')}
                    className="h-auto p-0 font-semibold hover:bg-transparent"
                  >
                    Customer {getSortIcon('customer')}
                  </Button>
                </TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('mot_expiry')}
                    className="h-auto p-0 font-semibold hover:bg-transparent"
                  >
                    MOT Status {getSortIcon('mot_expiry')}
                  </Button>
                </TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSortedVehicles.map((vehicle) => {
                const motStatus = getMotStatus(vehicle);
                return (
                  <TableRow 
                    key={vehicle.id}
                    onClick={() => openVehicleDialog(vehicle)}
                    className="cursor-pointer hover:bg-gray-50 transition-colors"
                    title="Click to view/edit vehicle details"
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{getVehicleTypeIcon(vehicle.vehicle_type)}</span>
                        <div>
                          <div className="font-mono font-medium">
                            {vehicle.registration_number}
                          </div>
                          {vehicle.color && (
                            <div className="text-xs text-gray-500">{vehicle.color}</div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div className="font-medium">
                        {vehicle.make} {vehicle.model}
                      </div>
                      {vehicle.engine_size && (
                        <div className="text-xs text-gray-500">{vehicle.engine_size}</div>
                      )}
                    </TableCell>
                    
                    <TableCell>
                      {vehicle.year || <span className="text-gray-400">-</span>}
                    </TableCell>
                    
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-green-600" />
                        <div>
                          <div className="font-medium">{vehicle.customer_name}</div>
                          {vehicle.customer_email && (
                            <div className="text-xs text-gray-500">{vehicle.customer_email}</div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      {vehicle.contact_name ? (
                        <div className="text-sm">
                          {vehicle.contact_name}
                          {vehicle.contact_role && (
                            <div className="text-xs text-gray-500">({vehicle.contact_role})</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">No contact</span>
                      )}
                    </TableCell>
                    
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {motStatus.status === 'overdue' && <AlertTriangle className="w-4 h-4 text-red-500" />}
                        {motStatus.status === 'due_soon' && <Calendar className="w-4 h-4 text-orange-500" />}
                        <Badge 
                          variant={motStatus.color === 'red' ? 'destructive' : 'outline'}
                          className={
                            motStatus.color === 'orange' ? 'bg-orange-50 text-orange-700' :
                            motStatus.color === 'green' ? 'bg-green-50 text-green-700' : ''
                          }
                        >
                          {motStatus.label}
                        </Badge>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRefreshMot(vehicle);
                          }}
                          disabled={refreshingVehicle === vehicle.id}
                          title="Refresh MOT status from DVLA"
                          className="h-6 w-6 p-0"
                        >
                          <RefreshCw className={`w-4 h-4 ${refreshingVehicle === vehicle.id ? 'animate-spin' : ''}`} />
                        </Button>
                      </div>
                    </TableCell>
                    
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`/dashboard/customers/${vehicle.customer_id}`, '_blank');
                        }}
                        title="View Customer"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Vehicle Edit Dialog */}
      <Dialog open={isVehicleDialogOpen} onOpenChange={setIsVehicleDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Vehicle - {editingVehicle?.registration_number}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleVehicleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Registration with DVLA Lookup */}
              <div>
                <Label>Registration Number *</Label>
                <div className="flex gap-2">
                  <Input
                    value={vehicleFormData.registration_number || ''}
                    onChange={(e) => setVehicleFormData({...vehicleFormData, registration_number: e.target.value.toUpperCase()})}
                    placeholder="e.g. AB12 CDE"
                    className={getFieldClassName(vehicleFormData.registration_number)}
                    required
                  />
                  <Button type="button" onClick={handleDvlaLookup} disabled={isDvlaLookupLoading}>
                    <RefreshCw className={`w-4 h-4 ${isDvlaLookupLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>

              {/* Contact */}
              <div>
                <Label>Contact</Label>
                <Select value={vehicleFormData.contact_id || '__no_contact__'} onValueChange={(value) => setVehicleFormData({...vehicleFormData, contact_id: value})}>
                  <SelectTrigger className={getFieldClassName(vehicleFormData.contact_id)}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__no_contact__">No specific contact</SelectItem>
                    {contacts.map(c => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div><Label>Make *</Label><Input value={vehicleFormData.make || ''} onChange={(e) => setVehicleFormData({...vehicleFormData, make: e.target.value})} className={getFieldClassName(vehicleFormData.make)} required /></div>
              <div><Label>Model *</Label><Input value={vehicleFormData.model || ''} onChange={(e) => setVehicleFormData({...vehicleFormData, model: e.target.value})} className={getFieldClassName(vehicleFormData.model)} required /></div>
              <div><Label>Year</Label><Input value={vehicleFormData.year || ''} onChange={(e) => setVehicleFormData({...vehicleFormData, year: e.target.value})} className={getFieldClassName(vehicleFormData.year)} /></div>
              <div><Label>Color</Label><Input value={vehicleFormData.color || ''} onChange={(e) => setVehicleFormData({...vehicleFormData, color: e.target.value})} className={getFieldClassName(vehicleFormData.color)} /></div>
              <div><Label>Vehicle Type *</Label>
                <Select value={vehicleFormData.vehicle_type || 'car'} onValueChange={(value) => setVehicleFormData({...vehicleFormData, vehicle_type: value})}>
                  <SelectTrigger className={getFieldClassName(vehicleFormData.vehicle_type)}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="car">Car</SelectItem>
                    <SelectItem value="van">Van</SelectItem>
                    <SelectItem value="truck">Truck</SelectItem>
                    <SelectItem value="motorcycle">Motorcycle</SelectItem>
                    <SelectItem value="suv">SUV</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Fuel Type</Label><Input value={vehicleFormData.fuel_type || ''} onChange={(e) => setVehicleFormData({...vehicleFormData, fuel_type: e.target.value})} className={getFieldClassName(vehicleFormData.fuel_type)} /></div>
              <div><Label>Engine Size</Label><Input value={vehicleFormData.engine_size || ''} onChange={(e) => setVehicleFormData({...vehicleFormData, engine_size: e.target.value})} placeholder="e.g. 2.0L" className={getFieldClassName(vehicleFormData.engine_size)} /></div>
              <div><Label>Mileage</Label><Input type="number" value={vehicleFormData.mileage || ''} onChange={(e) => setVehicleFormData({...vehicleFormData, mileage: e.target.value})} className={getFieldClassName(vehicleFormData.mileage)} /></div>
              <div><Label>VIN</Label><Input value={vehicleFormData.vin_number || ''} onChange={(e) => setVehicleFormData({...vehicleFormData, vin_number: e.target.value})} className={getFieldClassName(vehicleFormData.vin_number)} /></div>
              <div><Label>MOT Expiry</Label><Input type="date" value={vehicleFormData.mot_expiry_date || ''} onChange={(e) => setVehicleFormData({...vehicleFormData, mot_expiry_date: e.target.value})} className={getFieldClassName(vehicleFormData.mot_expiry_date)} /></div>
              <div><Label>Insurance Expiry</Label><Input type="date" value={vehicleFormData.insurance_renewal_date || ''} onChange={(e) => setVehicleFormData({...vehicleFormData, insurance_renewal_date: e.target.value})} className={getFieldClassName(vehicleFormData.insurance_renewal_date)} /></div>
              <div><Label>Insurance Company</Label><Input value={vehicleFormData.insurance_company || ''} onChange={(e) => setVehicleFormData({...vehicleFormData, insurance_company: e.target.value})} className={getFieldClassName(vehicleFormData.insurance_company)} /></div>
            </div>
            
            <div><Label>Special Requirements</Label><Input value={vehicleFormData.special_requirements || ''} onChange={(e) => setVehicleFormData({...vehicleFormData, special_requirements: e.target.value})} className={getFieldClassName(vehicleFormData.special_requirements)} /></div>
            <div><Label>Internal Notes</Label><textarea className={`w-full p-2 border rounded-md ${getFieldClassName(vehicleFormData.notes)}`} rows={3} value={vehicleFormData.notes || ''} onChange={(e) => setVehicleFormData({...vehicleFormData, notes: e.target.value})} /></div>

            <div className="flex gap-2 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setIsVehicleDialogOpen(false)}>Cancel</Button>
              <Button type="submit">Save Changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VehiclesManagement;